<template>
  <div class="tasks-tab-container">
    <div v-if="!filteredRequestLoading">
      <div class="tasks-tab-dropdowns">
        <div class="tasks-tab-assignee">
          <NrSelect
            v-model="assigneeFilter"
            :clearable="false"
            :hideLabel="true"
            :options="requestStaffList"
            @option:selected="handleSelectedTaskFilter('assignee')"
          >
            <template v-slot:label>Filter tasks by assignee</template>
          </NrSelect>
        </div>
        <div class="tasks-tab-status">
          <NrSelect
            v-model="tasksStatusFilter"
            :clearable="false"
            :hideLabel="true"
            :options="taskStatuses"
            @option:selected="handleSelectedTaskFilter('status')"
          >
            <template v-slot:label>Filter tasks by status</template>
          </NrSelect>
        </div>
      </div>
      <div v-if="!tasks.length">
        <p class="tasks-tab-empty-message">
          {{ statusEmptyMessage }}
        </p>
      </div>
      <div v-if="tasks.length">
        <NrTaskCard
          v-for="task in tasks"
          :with-icons="true"
          :key="task.id"
          :status="task.status"
          :id="task.id"
          :identifier="task.user.name_with_email"
          :date="taskDateByStatus(task)"
          :hasActionPermission="$can('read', 'staff_only_items')"
          @handle-edit="handleEditTask"
          @handle-delete="handleDeleteTask"
          @handle-complete="handleTaskCompletion"
        >
          <!-- task.description is sanitized on the backend: https://github.com/nextrequest/nextrequest/blob/726ba59/app/views/client/tasks/index.json.jbuilder#L11 -->
          <span v-html="task.description"></span>
        </NrTaskCard>
        <NrModal
          class="tasks-tab-delete-modal"
          title="Delete task"
          primary-button-text="Delete"
          :show="showDeleteTaskModal"
          hasSecondaryButton
          @on-primary-click="confirmTaskDelete"
          @on-close-click="handleCloseTaskDelete"
          @on-secondary-click="handleCloseTaskDelete"
        >
          <p>
            You are about to delete a task. This action cannot be undone. Do you
            want to continue?
          </p>
        </NrModal>
      </div>
      <div class="tasks-tab-pagination" v-if="tasks.length">
        <NrPagination
          :total="currentRequestTasksCount"
          :current="tasksPageNum"
          :per-page="tasksPerPage"
          aria-next-label="Next page"
          aria-previous-label="Previous page"
          aria-page-label="Page"
          aria-current-label="Current page"
          @change="setPageNum"
        />
      </div>
    </div>
    <NrLoadingAnimation v-else :count="3" />
  </div>
</template>
<script>
import NrSelect from '@nextrequestco/components/src/components/NrSelect/nr-select.vue'
import NrTaskCard from '@nextrequestco/components/src/components/NrTaskCard/nr-task-card.vue'
import NrModal from '@nextrequestco/components/src/components/NrModal/nr-modal.vue'
import NrLoadingAnimation from '@nextrequestco/components/src/components/NrLoadingAnimation/nr-loading-animation.vue'
import NrPagination from '@nextrequestco/components/src/components/NrPagination/nr-pagination.vue'
import { mapGetters, mapActions, mapState } from 'vuex'
import { uniqBy } from 'lodash'
const DEFAULT_ASSIGNEE = { id: 0, label: 'All assignees' }
const DEFAULT_STATUS = { id: 'all_tasks', label: 'All tasks' }
export default {
  name: 'TaskTab',
  components: {
    NrSelect,
    NrTaskCard,
    NrModal,
    NrLoadingAnimation,
    NrPagination
  },
  props: {
    tasks: {
      type: Array
    },
    timelineFilters: {
      type: Array
    },
    currentTimelinePageNum: {
      type: Number
    }
  },
  data() {
    return {
      assigneeFilter: DEFAULT_ASSIGNEE,
      currentTaskStatus: '',
      currentAssignee: '',
      taskToDelete: null,
      tasksPerPage: 25,
      statusType: null,
      assigneeUser: null,
      showDeleteTaskModal: false,
      currentTasksPageNum: 1,
      tasksStatusFilter: DEFAULT_STATUS
    }
  },

  watch: {
    tasksStatusFilter() {
      this.currentTasksPageNum = 1
      const { type, assignee, pageNumber } = this.tasksPayload()
      this.statusType = type
      this.assigneeUser = assignee ? this.assigneeFilter.label : null
      this.refreshTasks({ type, assignee, pageNumber })
    },
    assigneeFilter() {
      this.currentTasksPageNum = 1
      this.refreshTasks(this.tasksPayload())
    }
  },

  computed: {
    ...mapGetters('request_show', [
      'requestStaff',
      'request',
      'currentRequestTasksCount'
    ]),

    ...mapState('request_show', ['filteredRequestLoading']),
    tasksPageNum: {
      set(tasksPageNum) {
        this.currentTasksPageNum = tasksPageNum
        this.refreshTasks(this.tasksPayload())
      },
      get() {
        return this.currentTasksPageNum
      }
    },
    taskStatuses() {
      return [
        { id: 'overdue', label: 'Overdue' },
        { id: 'complete', label: 'Complete' },
        { id: 'incomplete', label: 'Incomplete' },
        { id: 'due_soon', label: 'Due soon' },
        DEFAULT_STATUS
      ]
    },

    requestStaffList() {
      return uniqBy(
        [
          ...this.requestStaff.map((item) => {
            return {
              id: item.id,
              label: item.name_with_email
            }
          }),
          {
            id: this.request.poc.id,
            label: this.request.poc.name_with_email
          },
          DEFAULT_ASSIGNEE
        ],
        'id'
      )
    },

    statusEmptyMessage() {
      let status = this.statusType
      const user = this.assigneeUser
      if (status === 'due_soon') {
        status = 'due soon'
      }
      if (!this.tasks.length && user && !status) {
        return `No tasks assigned to ${user} found on this request`
      } else if (!this.tasks.length && user && status) {
        return `No ${status} tasks assigned to ${user} found on this request`
      } else if (!this.tasks.length && !user && status) {
        return `No ${status} tasks found on this request`
      } else {
        return `No tasks found on this request`
      }
    }
  },

  methods: {
    ...mapActions('request_show', [
      'editTask',
      'editTaskCompletion',
      'setDisplayCreateTaskMsgBox',
      'setCurrentAction',
      'setEditingTask',
      'deleteTask',
      'fetchTimeline',
      'fetchRequestTasks'
    ]),

    setPageNum(tasksPageNum) {
      this.currentTasksPageNum = tasksPageNum
      this.refreshTasks(this.tasksPayload())
    },

    timelinePayload() {
      return {
        filters: this.timelineFilters,
        page_number: this.currentTimelinePageNum
      }
    },

    taskDateByStatus(task) {
      return task.status === 'Complete' ? task.completed_date : task.due_date
    },

    tasksPayload() {
      const assignee =
        this.assigneeFilter && this.assigneeFilter.id === 0
          ? null
          : this.assigneeFilter.id
      const type =
        this.tasksStatusFilter && this.tasksStatusFilter.id === 'all_tasks'
          ? null
          : this.tasksStatusFilter && this.tasksStatusFilter.id
      const pageNumber = this.currentTasksPageNum
      return {
        assignee,
        type,
        pageNumber
      }
    },

    handleCloseTaskDelete() {
      this.showDeleteTaskModal = false
      this.taskToDelete = null
    },

    confirmTaskDelete() {
      this.showDeleteTaskModal = false
      const payload = {
        task_id: this.taskToDelete,
        request_id: this.request.pretty_id
      }
      this.deleteTask(payload)
        .then(() => {
          this.refreshTimeline(this.timelinePayload())
          this.refreshTasks(this.tasksPayload())
          this.$toasted.success('Successfully deleted a task.')
        })
        .catch(() => {
          this.$toasted.error('An error has occurred while deleting a task.')
        })
    },
    refreshTasks(val) {
      this.fetchRequestTasks({
        pretty_id: this.request.pretty_id,
        params: {
          user_id: val.assignee,
          task_status: val.type,
          page_number: val.pageNumber
        }
      })
    },

    // eslint-disable-next-line camelcase
    refreshTimeline({ filters, page_number }) {
      const categories = filters.map((item) => item.label.toLowerCase())
      if (categories.includes('all timeline events')) {
        this.fetchTimeline({
          pretty_id: this.request.pretty_id,
          filter_categories: [
            'messages',
            'notes',
            'documents',
            'status',
            'due date',
            'tasks',
            'staff',
            'visibility',
            'tags',
            'invoices',
            'hours'
          ],
          page_number
        })
      } else {
        this.fetchTimeline({
          pretty_id: this.request.pretty_id,
          filter_categories: categories,
          page_number
        })
      }
    },

    handleEditTask(val) {
      const taskToEdit = this.tasks.filter((item) => item.id === val)
      this.setEditingTask(taskToEdit[0])
      this.setCurrentAction('editingTask')
      this.setDisplayCreateTaskMsgBox({ display: true, type: 'edit' })
    },
    handleDeleteTask(val) {
      this.showDeleteTaskModal = true
      this.taskToDelete = val
    },
    handleTaskCompletion(val) {
      const taskToEdit = this.tasks.find((item) => item.id === val)
      const payload = {
        action_type: 'edit',
        completed: !taskToEdit.completed,
        request_id: this.request.pretty_id,
        task_id: val
      }
      this.editTaskCompletion(payload)
    },

    handleSelectedTaskFilter(val) {
      if (
        val === 'status' &&
        this.tasksStatusFilter.label === this.currentTaskStatus
      ) {
        this.tasksStatusFilter = {}
        this.currentTaskStatus = ''
      } else if (
        val === 'assignee' &&
        this.assigneeFilter.label === this.currentAssignee
      ) {
        this.assigneeFilter = DEFAULT_ASSIGNEE
        this.currentAssignee = ''
      } else {
        if (val === 'status') {
          this.currentTaskStatus = this.tasksStatusFilter.label
        } else if (val === 'assignee') {
          this.currentAssignee = this.assigneeFilter.label
        }
      }
      if (this.assigneeFilter.label === 'All assignees') {
        this.assigneeFilter = DEFAULT_ASSIGNEE
        this.currentAssignee = ''
      }
    }
  }
}
</script>

<style lang="scss">
@import '@nextrequestco/components/src/styles/tokens/tokens.scss';
.tasks-tab-container {
  .tasks-tab-empty-message {
    padding: $spacing-12;
    color: $color-gray-80;
    text-align: center;
    font-size: $font-size-5;
  }
  .tasks-tab-dropdowns {
    display: flex;
    padding: $spacing-8;
    .tasks-tab-assignee {
      flex: 2;
      margin-right: $spacing-8;
    }
    .tasks-tab-status {
      flex: 1;
    }
  }
  .tasks-tab-delete-modal {
    .nr-modal__header {
      background-color: $color-red-100;
      .nr-modal__title {
        color: $color-white;
      }
    }
    .nr-modal__footer {
      .modal__btn-primary {
        background-color: $color-red-100;
      }
    }
  }
  .tasks-tab-pagination {
    display: flex;
    justify-content: flex-end;
    padding: $spacing-12;
  }
}
</style>
